// IM-EXT-016 (IM-UI-073) — Segmentação e atratividade de mercado: lista das análises e criação. A análise organiza os
// mercados, segmentos e subsegmentos a partir do contexto confirmado, das empresas-alvo, dos sinais e dos concorrentes, e
// mostra onde há mais oportunidade com um score que não esconde a composição.
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEGMENT_CLASS_LABEL, type SegmentClass } from '@intelimove/contracts';
import { ModuleIntro } from '../components/Guidance';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead, StatusPill } from '../components/Widgets';
import { SEGMENTATION_GUIDE } from '../data/guides';
import { CLASS_TONE, fmtDate, segmentationApi } from '../data/segmentation';
import { useResource } from '../hooks/useResource';
import { useCanManage } from './Targets';

const SHOWN: SegmentClass[] = ['PRIORITARIO', 'PROMISSOR', 'OBSERVAR', 'DADOS_INSUFICIENTES', 'NAO_RECOMENDADO'];

export function Segmentation() {
  const canManage = useCanManage();
  const [creating, setCreating] = useState(false);
  const { state, reload } = useResource((s) => segmentationApi.list(s), [], (d) => d.items.length === 0);
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Concorrentes e mercado" title="Segmentação e atratividade"
        subtitle="Em quais mercados, segmentos e regiões há mais oportunidade para a sua empresa — com cada nota explicada, o que é observado e o que é inferência."
        actions={canManage && !creating ? <button className="cta" onClick={() => setCreating(true)}>Nova análise</button> : undefined} />
      <ModuleIntro guide={SEGMENTATION_GUIDE} />
      {creating && <CreateForm onCancel={() => setCreating(false)} />}
      <AsyncView state={state} onRetry={reload}
        empty={<EmptyState title="Nenhuma análise de segmentação ainda" action={canManage && !creating ? <button className="cta" onClick={() => setCreating(true)}>Criar a primeira análise</button> : undefined}>
          A análise usa o contexto confirmado (ofertas, ICPs, restrições), as empresas-alvo, os sinais verificáveis e os concorrentes. Quanto mais dados, maior a cobertura de cada segmento.
          {!canManage && ' Um administrador pode criar a análise.'}</EmptyState>}>
        {(d) => (
          <div className="table-card">
            <div className="table-wrap">
              <table className="data-table stack">
                <caption className="sr-only">Análises de segmentação</caption>
                <thead><tr><th scope="col">Análise</th><th scope="col">Região</th><th scope="col">Segmentos</th><th scope="col">Destaques</th><th scope="col">Atualizada</th><th scope="col"><span className="sr-only">Ações</span></th></tr></thead>
                <tbody>
                  {d.items.map((x) => (
                    <tr key={x.id}>
                      <td data-label="Análise"><b>{x.name}</b><span className="cell-sub">versão {x.versionNo}{x.status === 'ARCHIVED' ? ' · arquivada' : ''}</span></td>
                      <td data-label="Região">{x.regionLabel}</td>
                      <td data-label="Segmentos"><span className="seg-counts">{SHOWN.filter((k) => x.counts[k]).map((k) => <StatusPill key={k} tone={CLASS_TONE[k]}>{x.counts[k]} {SEGMENT_CLASS_LABEL[k].toLowerCase()}</StatusPill>)}</span>
                        <span className="cell-sub">{x.counts.total} no total · estado atual, com as decisões da equipe</span></td>
                      <td data-label="Destaques">{x.top.length ? x.top.map((t) => <span key={t.name} className="cell-sub wide">{t.name} · {t.score ?? '—'}</span>) : <span className="cell-sub">nenhum prioritário ou promissor</span>}</td>
                      <td data-label="Atualizada">{fmtDate(x.updatedAt, true)}</td>
                      <td><Link className="small-action" to={`/segmentacao/${x.id}`}>Abrir</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </AsyncView>
    </section>
  );
}

function CreateForm({ onCancel }: { onCancel: () => void }) {
  const toast = useToast();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const name = String(f.get('name') ?? '').trim();
    const region = String(f.get('region') ?? '').trim();
    if (name.length < 3) { setError('Dê um nome com ao menos 3 caracteres.'); return; }
    setBusy(true); setError(null);
    try {
      const d = await segmentationApi.create(name, region ? { text: region } : undefined);
      toast(`Análise criada: ${d.segments.length} segmento(s) avaliados.`);
      nav(`/segmentacao/${d.id}`);
    } catch (err) { setError((err as Error).message); setBusy(false); }
  };
  return (
    <form className="card seg-create" onSubmit={submit} aria-labelledby="seg-new">
      <h2 className="card-title" id="seg-new">Nova análise de segmentação</h2>
      <p className="cell-sub wide">O InteliMove organiza os segmentos a partir do que já existe: ICPs e mercados confirmados, análises de mercado, empresas-alvo e onde os concorrentes atuam. Nada é estimado sem fonte.</p>
      <div className="seg-create-grid">
        <div className="field"><label htmlFor="seg-name">Nome</label><input id="seg-name" name="name" required minLength={3} maxLength={120} placeholder="Ex.: Plano comercial 2027" data-autofocus aria-invalid={!!error || undefined} /></div>
        <div className="field"><label htmlFor="seg-region">Região (opcional)</label><input id="seg-region" name="region" maxLength={200} placeholder="Brasil, um estado (PR) ou cidade (Curitiba/PR)" /></div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="form-actions"><button type="button" className="secondary" onClick={onCancel} disabled={busy}>Cancelar</button><button className="cta" disabled={busy}>{busy ? 'Calculando…' : 'Criar e calcular'}</button></div>
    </form>
  );
}
